import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import WeeklyBarsStaff from '../../../../components/disponibilidad/WeeklyBarsStaff';
import {
  useDisponibilidadStaff,
  DisponibilidadStaffRow,
} from '../../../../hooks/useDisponibilidadStaff';
import { useUsuarios, Usuario } from '../../../../hooks/useUsuarios';
import type { Row } from '../../../../components/disponibilidad/WeeklyBarsStaff';
import Autocomplete from '@mui/material/Autocomplete';

// Roles que se consideran "staff" (excluyendo profesor, cliente y consultor)
const STAFF_ROLES = [
  'admin',
  'hr',
  'admisiones',
  'att_familias',
  'att_alumnado',
  'att_profesorado',
  'facturacion',
];

export default function StaffDisponibilidadPage() {
  const { t } = useTranslation<'translation'>();
  const [selectedUserId, setSelectedUserId] = useState<number | ''>('');
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [searchUser, setSearchUser] = useState('');
  const [localRows, setLocalRows] = useState<Row[]>([]);


  // Obtener usuarios reales (solo staff, activos)
  const {
    rows: usuarios,
    loading: loadingUsuarios,
    error: usuariosError,
  } = useUsuarios({
    sinClientes: true,
    soloActivos: true,
    auto: true,
  });

  // Filtrar solo usuarios staff (excluir profesor, cliente y consultor)
  const staffUsuarios = useMemo(() => {
    return usuarios
      .filter(
        (usuario) =>
          STAFF_ROLES.includes(usuario.rol) &&
          usuario.rol !== 'profesor' &&
          usuario.rol !== 'cliente' &&
          usuario.rol !== 'consultor',
      )
      .sort((a, b) => {
        // Ordenar por nombre o email
        const nameA = a.nombreCompleto || a.email;
        const nameB = b.nombreCompleto || b.email;
        return nameA.localeCompare(nameB);
      });
  }, [usuarios]);

  // Hook para la disponibilidad del usuario seleccionado
  const {
    rows: disponibilidadRows,
    loading: loadingDisponibilidad,
    error: disponibilidadError,
    refresh,
    create,
    update,
    remove,
  } = useDisponibilidadStaff(selectedUserId as number);

  // Cargar disponibilidad cuando se selecciona un usuario
  useEffect(() => {
    if (selectedUserId) {
      const user = staffUsuarios.find((u) => u.id === selectedUserId);
      setSelectedUser(user || null);
      refresh();
    } else {
      setSelectedUser(null);
    }
  }, [selectedUserId, staffUsuarios, refresh]);
useEffect(() => {
  if (!loadingDisponibilidad && selectedUserId) {
    setLocalRows(
      disponibilidadRows.map((r) => ({
        id: r.id,
        diaSemana: r.diaSemana as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        horaInicio: r.horaInicio.slice(0, 5),
        horaFin: r.horaFin.slice(0, 5),
        comentario: r.comentario,
      }))
    );
  } else if (!selectedUserId) {
    setLocalRows([]);
  }
}, [disponibilidadRows, loadingDisponibilidad, selectedUserId]);

  const handleUserChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedUserId(value === '' ? '' : Number(value));
  };

  const handleCreate = async (rowData: Omit<Row, 'id'>) => {
    if (!selectedUserId) return;
    await create({
      idUsuario: selectedUserId,
      diaSemana: rowData.diaSemana,
      horaInicio: rowData.horaInicio,
      horaFin: rowData.horaFin,
      comentario: rowData.comentario ?? undefined,
    });
  };

  const handleUpdate = async (id: number, rowData: Partial<Row>) => {
    // 1️⃣ Actualiza SOLO en frontend (fluido)
    setLocalRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...rowData } : r)));

    // 2️⃣ Actualiza en backend SOLO cuando se suelta
    await update(id, {
      ...rowData,
      comentario: rowData.comentario ?? undefined,
    });
  };
const handleDelete = async (id: number) => {
  try {
    // 1. Eliminación optimista en el estado local
    setLocalRows(prev => {
      const nuevas = prev.filter(r => r.id !== id);
      return nuevas;
    });

    // 2. Llamar al backend
    await remove(id);
    
    // 3. Opcional: Forzar recarga para sincronización completa
    // await refresh();
    
  } catch (err) {
    // Si hay error, recargar datos del servidor
    await refresh();
  }
};

// También actualizar el efecto de carga:
useEffect(() => {
  if (!loadingDisponibilidad && selectedUserId) {
    setLocalRows(
      disponibilidadRows.map((r) => ({
        id: r.id,
        diaSemana: r.diaSemana as 0 | 1 | 2 | 3 | 4 | 5 | 6,
        horaInicio: r.horaInicio.slice(0, 5),
        horaFin: r.horaFin.slice(0, 5),
        comentario: r.comentario,
      }))
    );
  } else if (!selectedUserId) {
    setLocalRows([]);
  }
}, [disponibilidadRows, loadingDisponibilidad, selectedUserId]);

const formatUserName = (user: Usuario) => {
  if (user.nombreCompleto) {
    return `${user.nombreCompleto} (${user.email}) - ${user.rol}`;
  }
  return `${user.email} - ${user.rol}`;
};

  // Traducción de roles para mostrar
  const translateRole = (role: string) => {
    const roleTranslations: Record<string, string> = {
      admin: t('roles.admin', 'Administrador'),
      hr: t('roles.hr', 'Recursos Humanos'),
      admisiones: t('roles.admisiones', 'Admisiones'),
      att_familias: t('roles.att_familias', 'Atención a Familias'),
      att_alumnado: t('roles.att_alumnado', 'Atención al Alumnado'),
      att_profesorado: t('roles.att_profesorado', 'Atención al Profesorado'),
      facturacion: t('roles.facturacion', 'Facturación'),
      profesor: t('roles.profesor', 'Profesor'),
      cliente: t('roles.cliente', 'Cliente'),
      consultor: t('roles.consultor', 'Consultor'),
    };
    return roleTranslations[role] || role;
  };

  // Resumen de disponibilidad
  const disponibilidadResumen = useMemo(() => {
    const dias = [0, 1, 2, 3, 4, 5, 6];
    return dias.map((dia) => ({
      dia,
      count: disponibilidadRows.filter((r) => r.diaSemana === dia).length,
      horas: disponibilidadRows
        .filter((r) => r.diaSemana === dia)
        .map((r) => `${r.horaInicio.slice(0, 5)}-${r.horaFin.slice(0, 5)}`)
        .join(', '),
    }));
  }, [disponibilidadRows]);

  // Manejo de errores combinados
  const error = disponibilidadError || usuariosError;
  const loading = loadingDisponibilidad || loadingUsuarios;

  const handleRefresh = () => {
  if (selectedUserId) {
    refresh();
  }
};

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
    <Paper elevation={0} sx={{ 
      p: 3, 
      mb: 4, 
      border: 1, 
      borderColor: '#21A598', 
      borderRadius: 2,
      width: '100%', // Ocupa todo el ancho
      boxSizing: 'border-box',
      overflow: 'hidden' // Importante: oculta el desbordamiento
    }}>
        <>
          {/* Título */}
          <Typography variant="h4" gutterBottom sx={{ color: '#21A598', fontWeight: 700 }}>
            {t('disponibilidadStaff.titulo', 'Gestión de Disponibilidad del Staff')}
          </Typography>

          {/* Descripción */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t(
              'disponibilidadStaff.descripcion',
              'Gestiona las franjas horarias de disponibilidad del personal administrativo y de apoyo.',
            )}
          </Typography>

          {/* Selector de usuario */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Autocomplete
              sx={{ minWidth: 350 }}
              disabled={loadingUsuarios}
              options={staffUsuarios}
              value={selectedUser || null}
              autoHighlight
              getOptionLabel={(u) => formatUserName(u)}
              noOptionsText={t('disponibilidadStaff.noResultados', 'Sin resultados')}
              onInputChange={(_, v) => setSearchUser(v.toLowerCase())}
              onChange={(_, newValue) => {
                setSelectedUserId(newValue ? newValue.id : '');
                setSelectedUser(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('disponibilidadStaff.seleccionarStaff', 'Seleccionar Staff')}
                />
              )}
              filterOptions={(options) =>
                options.filter((u) => formatUserName(u).toLowerCase().includes(searchUser))
              }
            />

            {selectedUserId && (
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={loading}
              >
                {t('disponibilidadStaff.actualizar', 'Actualizar')}
              </Button>
            )}
          </Stack>

          {/* Error al cargar usuarios */}
          {usuariosError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {t('disponibilidadStaff.errorCargarUsuarios', 'Error al cargar la lista de usuarios')}
              : {usuariosError instanceof Error ? usuariosError.message : String(usuariosError)}
            </Alert>
          )}

          {/* Información del usuario seleccionado */}
          {selectedUser && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Typography variant="h6">
                  {selectedUser.nombreCompleto || selectedUser.email}
                </Typography>
                <Chip label={selectedUser.email} size="small" variant="outlined" />
                <Chip
                  label={translateRole(selectedUser.rol)}
                  size="small"
                  color="primary"
                  variant="filled"
                />
                <Chip
                  label={
                    selectedUser.activo
                      ? t('common.activo', 'Activo')
                      : t('common.inactivo', 'Inactivo')
                  }
                  size="small"
                  color={selectedUser.activo ? 'success' : 'default'}
                  variant="outlined"
                />
                {selectedUser.fechaIncorporacion && (
                  <Chip
                    label={`${t('common.fechaIncorporacion', 'Incorporación')}: ${
                      selectedUser.fechaIncorporacion
                    }`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Paper>
          )}

          {/* Mensajes de error de disponibilidad */}
          {error && !usuariosError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {t(
                'disponibilidadStaff.errorCargarDisponibilidad',
                'Error al cargar la disponibilidad',
              )}
              : {error instanceof Error ? error.message : String(error)}
            </Alert>
          )}

          {/* Loader */}
          {loading && selectedUserId && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Gráfico de disponibilidad */}
          {selectedUserId && !loading && (
            <>
              <WeeklyBarsStaff
                rows={localRows}
                title={`${t(
                  'disponibilidadStaff.disponibilidadDe',
                  'Disponibilidad de',
                )} ${selectedUser?.nombreCompleto || selectedUser?.email}`}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                idUsuario={selectedUserId}
              />
              {/* Resumen */}
              <Paper sx={{ p: 3, mt: 3, border: 1, borderColor: '#e0e0e0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#21A598' }}>
                  {t('disponibilidadStaff.resumen', 'Resumen de Disponibilidad')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                  {disponibilidadResumen.map((item) => (
                    <Chip
                      key={item.dia}
                      label={`${[
                        t('dias.lunes'),
                        t('dias.martes'),
                        t('dias.miercoles'),
                        t('dias.jueves'),
                        t('dias.viernes'),
                        t('dias.sabado'),
                        t('dias.domingo'),
                      ][item.dia].substring(
                        0,
                        3,
                      )}: ${item.count} ${t('disponibilidadStaff.franjas', 'franjas')}`}
                      variant={item.count > 0 ? 'filled' : 'outlined'}
                      color={item.count > 0 ? 'primary' : 'default'}
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Stack>
              </Paper>
            </>
          )}

          {/* Sin usuario seleccionado */}
          {!selectedUserId && !loadingUsuarios && staffUsuarios.length === 0 && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              {t(
                'disponibilidadStaff.noStaffUsuarios',
                'No se encontraron usuarios del staff activos.',
              )}
            </Alert>
          )}

          {!selectedUserId && staffUsuarios.length > 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {t(
                'disponibilidadStaff.seleccioneUsuarioInstrucciones',
                'Seleccione un usuario del staff para ver y gestionar su disponibilidad.',
              )}
            </Alert>
          )}
        </>
      </Paper>
    </Container>
  );
}
